import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'The Power of Cultural Values',
    content: [
      '<strong>Objective:</strong> Recognize how cultural orientations shape decision-making, relationships, and well-being.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Collectivism – valuing family and community over self',
      '• Power Distance – respecting hierarchy and authority',
      '• Uncertainty Avoidance – preference for order and stability',
      '• Masculinity vs. Femininity – gender roles and balance of strength and care',
      '• Long-Term Orientation – future planning and perseverance',
      '<br/>',
      '<strong>Discussion:</strong> Culture influences how we think, feel, and act – even when we\'re not aware of it. The Cultural Values Scale identifies patterns such as collectivism, power distance, and long-term orientation that reflect how people live and interact within a society.'
    ]
  },
  {
    title: 'Collectivism',
    content: [
      'Collectivism values family and community over self. It emphasizes group harmony, interdependence, and shared responsibilities.',
      '<br/>',
      'In Filipino culture, this is seen in bayanihan (community cooperation) and close family ties.'
    ]
  },
  {
    title: 'Power Distance',
    content: [
      'Power distance refers to respecting hierarchy and authority. It affects how we interact with elders, leaders, and authority figures.',
      '<br/>',
      'High power distance cultures value respect for authority and age.'
    ]
  },
  {
    title: 'Uncertainty Avoidance',
    content: [
      'Uncertainty avoidance is a preference for order and stability. It involves planning, rules, and minimizing risks.',
      '<br/>',
      'This helps reduce anxiety in unpredictable situations.'
    ]
  },
  {
    title: 'Masculinity vs. Femininity',
    content: [
      'This dimension is about gender roles and balance of strength and care. Masculine cultures value achievement and assertiveness; feminine cultures value care and quality of life.',
      '<br/>',
      'Balance is key for well-being.'
    ]
  },
  {
    title: 'Long-Term Orientation',
    content: [
      'Long-term orientation means future planning and perseverance. It involves saving, education, and long-term goals.',
      '<br/>',
      'This fosters resilience and stability.'
    ]
  },
  {
    title: 'Activity and Discussion',
    content: [
      'Rank which cultural values you see most in your life. Which ones help your well-being? Which might limit your growth?',
      '<br/>',
      'Discussion Prompt: "How do Filipino cultural traits (e.g., pakikisama, utang na loob, bayanihan) influence how we handle stress and relationships?"'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        'Reflect on how these cultural values shape your daily life and coping strategies.'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'Cultural values are neither "right" nor "wrong" – they\'re tools. The goal is to use them with awareness and balance.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 3: The Power of Cultural Values! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about cultural values.',
        'The quiz will help you review key concepts about the CVSCALE dimensions.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/religio-traditio-cultural-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/religio-traditio-cultural-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">The Power of Cultural Values</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Recognize how cultural orientations shape decision-making, relationships, and well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule3Page;


